/**
 * Single source of truth for HR Reports module.
 * Drives: name, description, filters, columns, data source, export.
 */

const EMP_FIELDS = [
  "agency",
  "surname",
  "Firstname",
  "Middlename",
  "employmentType",
  "position",
  "position.position_title",
];

const ATTENDEES_FIELDS = [
  "Firstname",
  "Middlename",
  "surname",
  "training",
  "date",
  "time",
  "stats",
];

/** Default attendance columns for Custom Timekeeping Report */
export const TIMEKEEPING_COLUMN_OPTIONS = [
  { key: "date", label: "Date" },
  { key: "employeeName", label: "Employee" },
  { key: "employeeNo", label: "Employee No." },
  { key: "timeIn", label: "Time In" },
  { key: "timeAmOut", label: "AM Out" },
  { key: "timePmIn", label: "PM In" },
  { key: "timeOut", label: "Time Out" },
  { key: "basicLateMinutes", label: "Late (mins)" },
  { key: "basicUndertimeMinutes", label: "Undertime (mins)" },
  { key: "timeRecStats", label: "Status" },
  { key: "scheduleLabel", label: "Schedule" },
];

const DATE_RANGE = {
  key: "dateRange",
  type: "dateRange",
  required: true,
  label: "Date Range",
};
const EMPLOYEE = {
  key: "employee",
  type: "employee",
  required: false,
  label: "Employee",
};

const SEPARATION_TYPE_OPTIONS = [
  "Resigned",
  "Transferred",
  "Terminated",
  "Nonrenewal",
  "End of term",
  "End of appointment",
  "Death",
  "Early retirement",
  "Compulsory Retirement",
  "Optional Retirement",
  "Dismissed from the service",
  "Dropped from the rolls",
  "Suspended",
  "Separated",
];

const SEPARATION_TYPE = {
  key: "type",
  type: "select",
  required: false,
  label: "Type",
  options: SEPARATION_TYPE_OPTIONS.map((value) => ({
    label: value,
    value,
  })),
};

const HIERARCHY = {
  key: "hierarchy",
  type: "hierarchy",
  required: false,
  label: "Office / Organization",
};
const MONTH_YEAR = [
  { key: "month", type: "month", required: true, label: "Month" },
  { key: "year", type: "year", required: true, label: "Year" },
];

function cols(definitions) {
  return definitions.map(([key, label, type = "String"]) => ({
    key,
    label,
    type,
  }));
}

/** Build a Table/export schema from column definitions */
export function buildSchemaFromColumns(columns, collection = "report_rows") {
  const fields = {};
  (columns || []).forEach((col) => {
    fields[col.key] = {
      type: col.type || "String",
      label: col.label || col.key,
    };
  });
  return { collection, fields };
}

export function getReportByKey(key) {
  return REPORT_MENUS.find((r) => r.key === key) || null;
}

export function getReportDisplayFields(report) {
  if (!report) return [];
  if (report.mode === "collection") return report.displayField || [];
  return (report.columns || []).map((c) => c.key);
}

const STANDARD_REPORTS = [
  // {
  //   key: "coc_availment",
  //   name: "COC Availment and Non-Availment of Employees",
  //   description:
  //     "Compensatory Overtime Credit (CTO) balances and availment activity.",
  //   category: "standard",
  //   mode: "service",
  //   service: "fetchCocReport",
  //   filters: [
  //     DATE_RANGE,
  //     EMPLOYEE,
  //     HIERARCHY,
  //     {
  //       key: "availmentStatus",
  //       type: "select",
  //       required: false,
  //       label: "Availment Status",
  //       options: [
  //         { label: "All", value: "" },
  //         { label: "Availed", value: "availed" },
  //         { label: "Non-Availed", value: "non_availed" },
  //       ],
  //     },
  //   ],
  //   columns: cols([
  //     ["employeeName", "Employee"],
  //     ["employeeNo", "Employee No."],
  //     ["cocEarned", "COC Earned"],
  //     ["cocAvailed", "COC Availed"],
  //     ["cocRemaining", "COC Remaining"],
  //     ["dateAvailed", "Date Availed"],
  //     ["status", "Status"],
  //   ]),
  //   export: { enabled: true },
  // },
  // {
  //   key: "custom_timekeeping",
  //   name: "Custom Timekeeping Report",
  //   description: "Flexible attendance report from biometric logs.",
  //   category: "standard",
  //   mode: "service",
  //   service: "fetchCustomTimekeepingReport",
  //   filters: [...MONTH_YEAR, EMPLOYEE, HIERARCHY],
  //   columns: TIMEKEEPING_COLUMN_OPTIONS,
  //   export: { enabled: true },
  // },
  {
    key: "daily_in_out",
    name: "Daily In/Out Report",
    description:
      "Daily clock-in/out from biometric logs, including overnight schedules.",
    category: "standard",
    mode: "service",
    service: "fetchDailyInOutReport",
    filters: [...MONTH_YEAR, EMPLOYEE, HIERARCHY],
    columns: cols([
      ["date", "Date"],
      ["employeeName", "Employee"],
      ["employeeNo", "Employee No."],
      ["timeIn", "Time In"],
      ["timeAmOut", "AM Out"],
      ["timePmIn", "PM In"],
      ["timeOut", "Time Out"],
      ["scheduleLabel", "Schedule"],
      ["timeRecStats", "Status"],
    ]),
    export: { enabled: true },
  },
  {
    key: "dtr_corrections",
    name: "DTR Corrections",
    description:
      "DTR punch corrections from biometric_logs (Time Management / Timesheet edits).",
    category: "standard",
    mode: "service",
    service: "fetchDtrCorrectionsReport",
    filters: [DATE_RANGE, EMPLOYEE, HIERARCHY],
    columns: cols([
      ["employeeName", "Employee"],
      ["employeeNo", "Employee No."],
      ["date", "Date"],
      ["punchType", "Punch Type"],
      ["mode", "Mode"],
      ["time", "Time"],
    ]),
    export: { enabled: true },
  },
  {
    key: "awol_employees",
    name: "List of AWOL Employees",
    description:
      "Employees tagged ABSENT after excluding approved leave, official business, holidays, and rest days.",
    category: "standard",
    mode: "service",
    service: "fetchAwolReport",
    filters: [...MONTH_YEAR, EMPLOYEE, HIERARCHY],
    columns: cols([
      ["employeeName", "Employee"],
      ["employeeNo", "Employee No."],
      ["awolDates", "AWOL Dates"],
      ["awolDays", "AWOL Days"],
      ["consecutiveAwolDays", "Consecutive AWOL Days"],
      ["lastAttendance", "Last Attendance"],
      ["status", "Status"],
    ]),
    export: { enabled: true },
  },
  {
    key: "official_business",
    name: "Official Business",
    description: "Official Business requests and approvals.",
    category: "standard",
    mode: "service",
    service: "fetchOfficialBusinessReport",
    // Match Finance → OB Management filters (no org hierarchy).
    filters: [
      {
        key: "dateRequested",
        type: "dateRange",
        required: false,
        label: "Filter by Date Requested",
        fields: ["createdAt", "dateApproved", ""],
      },
      {
        key: "obDates",
        type: "dateRange",
        required: false,
        label: "Filter by OB Dates",
        isArray: true,
        fields: ["obDates", ""],
      },
      { ...EMPLOYEE, label: "Select Employee" },
      {
        key: "status",
        type: "select",
        required: false,
        label: "Filter by status",
        options: [
          { label: "All", value: "" },
          { label: "Approved", value: "Approved" },
          { label: "Disapproved", value: "Disapproved" },
          { label: "Forwarded to CEBS", value: "Forwarded to CEBS" },
          { label: "Pending", value: "Pending" },
        ],
      },
    ],
    columns: cols([
      ["employeeName", "Employee"],
      ["obDate", "OB Date"],
      ["dateFrom", "Start"],
      ["dateTo", "End"],
      ["destination", "Destination"],
      ["purpose", "Purpose"],
      ["status", "Status"],
      ["dateApproved", "Date Approved"],
    ]),
    export: { enabled: true },
  },
  {
    key: "perfect_attendance",
    name: "Perfect Attendance",
    description:
      "No absences, no lates, no undertime. Approved Official Business does not disqualify.",
    category: "standard",
    mode: "service",
    service: "fetchPerfectAttendanceReport",
    filters: [...MONTH_YEAR, EMPLOYEE, HIERARCHY],
    columns: cols([
      ["employeeName", "Employee"],
      ["employeeNo", "Employee No."],
      ["daysPresent", "Days Present"],
      ["lateCount", "Lates"],
      ["undertimeCount", "Undertime"],
      ["absenceCount", "Absences"],
      ["obDays", "OB Days"],
      ["status", "Status"],
    ]),
    export: { enabled: true },
  },
  {
    key: "tardiness_frequency",
    name: "Tardiness Frequency Report",
    description:
      "Late frequency from biometric attendance, sorted by highest frequency.",
    category: "standard",
    mode: "service",
    service: "fetchTardinessFrequencyReport",
    filters: [...MONTH_YEAR, EMPLOYEE, HIERARCHY],
    columns: cols([
      ["employeeName", "Employee"],
      ["employeeNo", "Employee No."],
      ["lateDates", "Late Dates"],
      ["totalLateMinutes", "Total Late Minutes"],
    ]),
    export: { enabled: true },
  },
  {
    key: "leave_credits",
    name: "Leave Credits",
    description:
      "Current leave credit balances from leave_cred (per employee leave type).",
    category: "standard",
    mode: "service",
    service: "fetchLeaveCreditsReport",
    filters: [EMPLOYEE, HIERARCHY],
    columns: cols([
      ["employeeName", "Employee"],
      ["employeeNo", "Employee No."],
      ["leaveType", "Leave Type"],
      ["currentCredits", "Current Credits"],
    ]),
    export: { enabled: true },
  },
  {
    key: "travel_authority",
    name: "Personal Travel Abroad/Local (Travel Authority)",
    description:
      "Approved leave requests marked for travel abroad or within the Philippines.",
    category: "standard",
    mode: "service",
    service: "fetchTravelAuthorityReport",
    filters: [
      { ...DATE_RANGE, required: false, label: "Leave Date Range" },
      EMPLOYEE,
      HIERARCHY,
    ],
    columns: cols([
      ["employeeName", "Employee"],
      ["employeeNo", "Employee No."],
      ["leaveType", "Leave Type"],
      ["travelType", "Travel Type"],
      ["destination", "Destination / Specify"],
      ["dateFrom", "Date From"],
      ["dateTo", "Date To"],
      ["status", "Status"],
    ]),
    export: { enabled: true },
  },
];

const LEGACY_REPORTS = [
  {
    key: "contract_of_service",
    name: "Contract Of Service",
    description: "Employees under Contract of Service employment type.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS],
    where: {
      "employmentType.id": "491a1aaf-c314-4bc8-b393-71c3f56f70b0",
    },
    filters: [],
    export: { enabled: true },
  },
  {
    key: "employees_by_age",
    name: "Employees by Age Bracket",
    description: "Employees filtered by age bracket.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS],
    isAgeBracket: true,
    where: {},
    filters: [],
    export: { enabled: true },
  },
  {
    key: "employees_by_gender",
    name: "Employees By Gender",
    description: "Employees filtered by gender.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS, "sex"],
    where: {},
    filters: ["sex"],
    export: { enabled: true },
  },
  {
    key: "employees_by_civil",
    name: "Employees By Civil Status",
    description: "Employees filtered by civil status.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS, "civil"],
    where: {},
    filters: ["civil"],
    export: { enabled: true },
  },
  {
    key: "employees_contact",
    name: "Employees by Contact Information",
    description: "Employee contact information.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS, "mobile"],
    where: {},
    filters: [],
    export: { enabled: true },
  },
  {
    key: "employees_length_of_service",
    name: "Employees Length of Service",
    description: "Employees by hire date / length of service.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS, "hired"],
    isHiredFilter: true,
    where: {},
    filters: [],
    export: { enabled: true },
  },
  {
    key: "employees_movement",
    name: "Employees Movement History",
    description: "Employee movement / service history.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS],
    where: {},
    actionList: [{ label: "", actionType: "Service Report" }],
    filters: [],
    export: { enabled: true },
  },
  {
    key: "employees_training",
    name: "Employees Training and Education Report",
    description: "Training attendees report.",
    category: "legacy",
    mode: "collection",
    collection: "attendees",
    displayField: [...ATTENDEES_FIELDS],
    where: {},
    actionList: [{ label: "View", actionType: "view" }],
    filters: [],
    export: { enabled: true },
  },
  {
    key: "government_id",
    name: "Government ID",
    description: "Employee government IDs.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [
      ...EMP_FIELDS,
      "tin",
      "gsis",
      "sss",
      "philhealth",
      "pagIbig",
    ],
    where: {},
    filters: [],
    export: { enabled: true },
  },
  {
    key: "hired_employees",
    name: "Hired Employees",
    description: "Newly hired employees.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS, "hired", "month", "year"],
    where: {},
    isHiredFilter: true,
    filters: [],
    export: { enabled: true },
  },
  {
    key: "leave_balance",
    name: "Leave Balance",
    description: "Employee leave balances.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS],
    where: {},
    actionList: [{ label: "", actionType: "Leaves Report" }],
    filters: [],
    export: { enabled: true },
  },
  {
    key: "plantilla_item",
    name: "List of Plantilla Item",
    description: "Plantilla position titles.",
    category: "legacy",
    mode: "collection",
    collection: "position_titles",
    displayField: ["position_title", "salary_grade", "createdAt"],
    where: {},
    filters: [],
    export: { enabled: true },
  },
  {
    key: "overtime_requests",
    name: "Overtime Requests",
    description: "Overtime requests by date.",
    category: "legacy",
    mode: "collection",
    collection: "overtime",
    isDateFilter: true,
    filters: ["stats"],
    displayField: ["employee", "startTime", "endTime", "stats"],
    where: {},
    export: { enabled: true },
  },
  {
    key: "payroll_reports",
    name: "Payroll Reports",
    description: "View payroll-related reports.",
    category: "custom",
    mode: "component",
  },
  {
    key: "separated_employees",
    name: "Separated Employees",
    description: "Separated employees.",
    category: "legacy",
    mode: "collection",
    collection: "seperated",
    displayField: ["type", "dateSeparation", "employee", "cause"],
    where: {},
    filters: [SEPARATION_TYPE, EMPLOYEE],
    export: { enabled: true },
  },
  {
    key: "salary_adjustment",
    name: "Notice of Salary Adjusment",
    description: "Salary adjustment notices.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS],
    where: {},
    actionList: [{ label: "", actionType: "Salary Adjustment" }],
    filters: [],
    export: { enabled: true },
  },
  {
    key: "service_request_legacy",
    name: "Service Request",
    description: "Generic service requests (legacy).",
    category: "legacy",
    mode: "collection",
    collection: "requests",
    displayField: [
      "requestType",
      "description",
      "stats",
      "createdAt",
      "updatedAt",
      "employee",
    ],
    where: {},
    filters: [],
    export: { enabled: true },
  },
  {
    key: "step_increment",
    name: "Notice of Step Increment",
    description: "Step increment notices.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS],
    where: {},
    actionList: [{ label: "", actionType: "Step Increment" }],
    filters: [],
    export: { enabled: true },
  },
  {
    key: "solo_parent",
    name: "Solo Parent Employee",
    description: "Solo parent employees.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS],
    where: { questionEleven: { $ne: "", $exists: true } },
    filters: [],
    export: { enabled: true },
  },
  {
    key: "trainings_education",
    name: "Trainings and Education",
    description: "Training management records.",
    category: "legacy",
    mode: "collection",
    collection: "management_training",
    displayField: ["training", "date", "time"],
    where: {},
    filters: [],
    export: { enabled: true },
  },
  {
    key: "committee_structure",
    name: "Committee Structure",
    description: "Committee membership structure.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: ["surname", "Firstname", "Middlename", "position"],
    isCommitteeFilter: true,
    where: {},
    filters: [],
    export: { enabled: true },
  },
  {
    key: "organizational_structure",
    name: "Organizational Structure",
    description: "Employees by organization hierarchy.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS],
    isOrganizationFilter: true,
    where: {},
    filters: [],
    export: { enabled: true },
  },
  {
    key: "employee_records",
    name: "Employee records",
    description: "Full employee records export.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [...EMP_FIELDS, "positionLabel", "nonRegPosition"],
    filters: ["employmentType"],
    isOrganizationFilter: true,
    where: {},
    export: { enabled: true },
  },
  {
    key: "office_assignment",
    name: "Office Assignment",
    description: "Employee office assignments and biometric devices.",
    category: "legacy",
    mode: "collection",
    collection: "employees",
    displayField: [
      "agency",
      "surname",
      "Firstname",
      "Middlename",
      "region",
      "office",
      "division",
      "section",
      "unit",
      "cluster",
    ],
    where: {},
    actionList: [{ label: "", actionType: "Office Assignment" }],
    filters: ["agency", "surname", "Firstname", "Middlename"],
    export: { enabled: true },
  },
];

export const REPORT_MENUS = [...STANDARD_REPORTS, ...LEGACY_REPORTS];

export const DEFAULT_REPORT =
  REPORT_MENUS.find((report) => report.key === "employee_records") ||
  STANDARD_REPORTS[0];

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
